import React, { FC, useMemo, useRef, useState } from 'react'
import styles from '../TicketLanding.module.css'
import classNames from 'classnames'
import Autoplay from 'embla-carousel-autoplay'
import { motion, Variants } from 'framer-motion'
import { LinkButton } from '@/atoms/Button'
import { GuildIcon } from '@/atoms/Icons/GuildIcon'
import { Text } from '@/atoms/Text'
import { EmblaCarousel, EmblaSlide } from '@/molecules/EmblaCarousel'
import { useTranslate } from '@/utils/translate/translate-client'
import { GuildRedemptionCard } from '@/views/TicketsLandingView/NewTicketsLandingView/components/GuildRedemptionCard'
import { RadialGradient } from '@/views/TicketsLandingView/NewTicketsLandingView/components/RadialGradient'
import {
  ComingSoonTheatricalRelease,
  CurrentTheatricalRelease,
} from '@/views/TicketsLandingView/NewTicketsLandingView/types'

interface Props {
  releases: CurrentTheatricalRelease[]
  comingSoonReleases: ComingSoonTheatricalRelease[]
  className?: string
}

const sectionVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: 'easeOut',
    },
  },
}

export const GuildRedemptionUpsell: FC<Props> = ({ releases, comingSoonReleases, className }) => {
  const { t } = useTranslate('tickets')
  const sectionRef = useRef<HTMLDivElement>(null)
  const [selectedRelease, setSelectedRelease] = useState<CurrentTheatricalRelease | ComingSoonTheatricalRelease | null>(
    null,
  )

  const combinedReleases = useMemo(() => [...releases, ...comingSoonReleases], [comingSoonReleases, releases])

  const includeCarousel = combinedReleases.length > 1

  return (
    <motion.section
      ref={sectionRef}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      variants={sectionVariants}
      className={classNames('relative', className)}
    >
      <RadialGradient
        className="absolute right-0 top-1/2 -z-10 -translate-y-1/2 translate-x-1/2 opacity-10"
        from="50% 50% at 50% 50%, #F29F3D 0.13%"
        to="rgba(242, 159, 61, 0) 100%"
        size="200%"
      />

      <div className="flex flex-col text-center">
        <div className={styles.layoutContainer}>
          <div className="mx-auto flex max-w-[640px] flex-col gap-3 md:gap-6">
            <Text className="photon-title-md md:photon-heading-lg xl:photon-heading-xl" weight="bold" color="white">
              {t('enjoyYourGuildTickets', 'Enjoy Your Guild Tickets')}
            </Text>
            <Text color="core-gray-400" className="photon-paragraph-sm md:photon-paragraph-xl">
              {t(
                'enjoyYourGuildTicketsDescription',
                'Redeem them or gift them. As a Premium Guild member, you have access to two complimentary tickets to each Angel theatrical release.',
              )}
            </Text>
          </div>

          {!includeCarousel && (
            <div className="mt-8 grid md:mt-12">
              {combinedReleases.map((release) => {
                return (
                  <GuildRedemptionCard
                    containerRef={sectionRef}
                    className="mx-auto"
                    key={release.theatricalSlug}
                    release={release}
                  />
                )
              })}
            </div>
          )}

          {/*mobile*/}
          {includeCarousel && (
            <EmblaCarousel
              includeArrows
              rightArrowClassname="!right-0 translate-x-1/2"
              leftArrowClassname="!left-0 -translate-x-1/2"
              options={{ loop: true, align: 'center', slidesToScroll: 1 }}
              plugins={[Autoplay()]}
              className="mt-8 flex items-stretch md:hidden"
            >
              {combinedReleases.map((release, index) => {
                return (
                  <EmblaSlide
                    fadeOutOfView
                    className="mr-2 w-full max-w-[265px] sm:max-w-[280px]"
                    key={`${release.theatricalSlug}-${index}`}
                  >
                    <GuildRedemptionCard
                      containerRef={sectionRef}
                      isLoopingCard
                      setSelectedRelease={setSelectedRelease}
                      release={release}
                    />
                  </EmblaSlide>
                )
              })}
            </EmblaCarousel>
          )}
        </div>

        {/*desktop*/}
        {includeCarousel && (
          <EmblaCarousel
            includeArrows
            options={{ dragFree: true, align: 'start' }}
            className="mt-12 hidden items-stretch md:flex"
            containerClassName={classNames(
              styles.layoutContainer,
              combinedReleases.length == 2 && 'justify-center',
              combinedReleases.length === 3 && 'lg:justify-center',
            )}
          >
            {combinedReleases.map((release) => {
              return (
                <EmblaSlide
                  fadeOutOfView
                  className="mr-6 w-full max-w-[250px] xl:max-w-[316px]"
                  key={release.theatricalSlug}
                >
                  <GuildRedemptionCard containerRef={sectionRef} release={release} />
                </EmblaSlide>
              )
            })}
          </EmblaCarousel>
        )}

        <div className="mt-10 flex flex-col space-y-2 px-4 md:mx-auto md:mt-16 md:w-fit">
          {selectedRelease?.guildCodes && selectedRelease.guildCodes.length > 0 && (
            <LinkButton
              href={`/tickets/${selectedRelease.theatricalSlug}?discounts=${selectedRelease.guildCodes.join(',')}`}
              className="md:hidden"
              size="md"
              variant="white"
            >
              <GuildIcon className="md:size-6" size={20} color1="black" />
              {t('redeemGuildTickets', 'Redeem Guild Tickets')}
            </LinkButton>
          )}

          {selectedRelease && !selectedRelease.guildCodes && (
            <LinkButton href={selectedRelease.hubPageLink} className="md:hidden" size="md" variant="white">
              {t('learnMore', 'Learn More')}
            </LinkButton>
          )}

          <LinkButton external href="/account" size="md" variant="offwhite" className="!bg-transparent md:!bg-white/20">
            {t('manageMembership', 'Manage Membership')}
          </LinkButton>
        </div>
      </div>
    </motion.section>
  )
}
